import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lightbulb, SignOut, House, MegaphoneSimple, Package, Users } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const NAV_BY_ROLE = {
  agency_admin: [
    { to: "/app/agency", label: "Overview", icon: House },
    { to: "/app/agency/campaigns", label: "Campaigns", icon: Package },
    { to: "/app/agency/submissions", label: "All Submissions", icon: MegaphoneSimple },
    { to: "/app/agency/users", label: "Users", icon: Users },
  ],
  influencer: [
    { to: "/app/influencer", label: "My Submissions", icon: MegaphoneSimple },
    { to: "/app/influencer/campaigns", label: "Campaigns", icon: Package },
  ],
  brand: [
    { to: "/app/brand", label: "Review Queue", icon: MegaphoneSimple },
    { to: "/app/brand/campaigns", label: "Campaigns", icon: Package },
  ],
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = NAV_BY_ROLE[user?.role] || [];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-black text-zinc-50 flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-white/5 bg-black flex-shrink-0 hidden md:flex flex-col">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-yellow-400 rounded-lg flex items-center justify-center">
              <Lightbulb weight="fill" size={18} className="text-black" />
            </div>
            <span className="font-heading font-black text-lg">Mastra</span>
          </Link>
        </div>
        <nav className="px-3 flex-1 space-y-1">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-yellow-400/10 text-yellow-300 border border-yellow-400/20"
                      : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Icon size={18} weight="duotone" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="text-xs text-zinc-500 mb-2 font-mono uppercase">Signed in</div>
          <div className="font-heading font-bold text-sm truncate">{user?.name}</div>
          <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          <div className="mt-1 inline-block px-2 py-0.5 rounded bg-zinc-900 text-zinc-400 text-[10px] font-mono uppercase">
            {user?.role?.replace("_", " ")}
          </div>
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="outline"
            className="mt-3 w-full border-white/10 bg-transparent hover:bg-white/5 text-zinc-300 h-9"
          >
            <SignOut size={16} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="md:hidden border-b border-white/5 p-4 flex items-center justify-between">
          <span className="font-heading font-black">Mastra Media</span>
          <Button onClick={handleLogout} variant="ghost" size="sm" className="text-zinc-300">
            <SignOut size={16} />
          </Button>
        </header>
        <main className="p-6 md:p-10 max-w-7xl mx-auto">{children}</main>
      </div>
    </div>
  );
}
